import { useSyncExternalStore } from 'react';

// Persistent draft for the multi-step gift card flow. Without this every
// page kept its own useState and we passed values via react-router's
// location.state — which dropped fields on the way through (sendDate
// was lost going from recipient → delivery), and any back-navigation
// wiped form contents. Storing it in sessionStorage means going Back
// still shows the customer's previous answers and the summary page sees
// everything regardless of which entry point the user took.

export type GiftCardDraft = {
  amount?: number;
  customAmount?: string;
  // YYYY-MM-DD; empty = send immediately.
  sendDate?: string;
  // Recipient
  firstName?: string;
  lastName?: string;
  email?: string;
  message?: string;
  // Delivery
  method?: 'RECIPIENT' | 'SELF';
  occasion?: string;
  // Buyer (collected on summary / payment)
  buyerFirstName?: string;
  buyerLastName?: string;
  buyerEmail?: string;
};

const STORAGE_KEY = 'cherry.giftCardDraft.v1';

type Listener = () => void;
const listeners = new Set<Listener>();

function read(): GiftCardDraft | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as GiftCardDraft;
  } catch {
    return null;
  }
}

function write(value: GiftCardDraft) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // sessionStorage can throw in private-mode browsers — drop quietly.
  }
  for (const l of listeners) l();
}

export function getGiftCardDraft(): GiftCardDraft | null {
  return read();
}

export function patchGiftCardDraft(patch: Partial<GiftCardDraft>) {
  const current = read() ?? {};
  write({ ...current, ...patch });
}

export function clearGiftCardDraft() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  for (const l of listeners) l();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Snapshot cache so useSyncExternalStore doesn't churn on identical JSON.
let cachedSnapshot = '';
let cachedValue: GiftCardDraft | null = null;
function getSnapshot(): GiftCardDraft | null {
  const raw = (() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  })();
  if (raw === cachedSnapshot) return cachedValue;
  cachedSnapshot = raw;
  try {
    cachedValue = raw ? (JSON.parse(raw) as GiftCardDraft) : null;
  } catch {
    cachedValue = null;
  }
  return cachedValue;
}

export function useGiftCardDraft(): GiftCardDraft | null {
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}
